import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { reportsAPI } from '../../services/api';
import { generateNativePdf, printStyles, CompanyLetterhead } from './shared';

export function RMRequirementsReportPrintView() {
  const [requirements, setRequirements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get URL params for date range
        const urlParams = new URLSearchParams(window.location.search);
        const params = {};
        if (urlParams.get('start_date')) params.start_date = urlParams.get('start_date');
        if (urlParams.get('end_date')) params.end_date = urlParams.get('end_date');
        
        const res = await reportsAPI.rmRequirements(params);
        setRequirements(res.data.requirements || []);
        setSummary(res.data.summary || null);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading && requirements.length >= 0) {
      setTimeout(() => window.print(), 500);
    }
  }, [loading, requirements]);

  if (loading) return <div className="p-8 text-center">Loading RM requirements report...</div>;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="print-container" style={{ maxWidth: '210mm', margin: '0 auto', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
        .rm-table { width: 100%; border-collapse: collapse; margin-top: 15px; }
        .rm-table th, .rm-table td { border: 1px solid #e2e8f0; padding: 8px 10px; }
        .rm-table th { background: #1e40af; color: white; font-weight: bold; text-align: left; }
        .rm-table tr:nth-child(even) { background: #f8fafc; }
        .rm-table .total-row { background: #dbeafe !important; font-weight: bold; }
      `}</style>

      {/* Professional Header */}
      <div style={{ borderBottom: '3px solid #1e40af', paddingBottom: '15px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 'bold', color: '#1e40af' }}>SINGHANIA INTERNATIONAL LIMITED</h1>
            <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#64748b' }}>
              C-25, Phase-1, Focal Point, Dhandari Kalan, Ludhiana - 141010, Punjab, India
            </p>
            <p style={{ margin: '2px 0 0', fontSize: '10px', color: '#64748b' }}>
              Email: info@singhaniainternational.com | Phone: +91 161 4611418
            </p>
            <p style={{ margin: '4px 0 0', fontSize: '9px', color: '#059669' }}>
              An ISO 9001, ISO 14001, ISO 45001 and IATF 16949 certified company
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>RAW MATERIAL REQUIREMENTS</h2>
            <p style={{ margin: '5px 0 0', fontSize: '11px', color: '#64748b' }}>
              Status: Released & WIP Work Orders
            </p>
            <p style={{ margin: '2px 0 0', fontSize: '10px', color: '#64748b' }}>Generated: {formatDate(new Date())}</p>
          </div>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
          <div style={{ flex: 1, background: '#f0f9ff', padding: '12px', borderRadius: '6px', border: '1px solid #bae6fd', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#0369a1' }}>{summary.total_weight_kg?.toLocaleString()} Kg</div>
            <div style={{ fontSize: '10px', color: '#64748b' }}>Total Weight Required</div>
          </div>
          <div style={{ flex: 1, background: '#f0fdf4', padding: '12px', borderRadius: '6px', border: '1px solid #bbf7d0', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#166534' }}>{summary.total_orders}</div>
            <div style={{ fontSize: '10px', color: '#64748b' }}>Work Orders</div>
          </div>
          <div style={{ flex: 1, background: '#faf5ff', padding: '12px', borderRadius: '6px', border: '1px solid #e9d5ff', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#7c3aed' }}>{summary.total_qty?.toLocaleString()}</div>
            <div style={{ fontSize: '10px', color: '#64748b' }}>Total Quantity</div>
          </div>
          <div style={{ flex: 1, background: '#fef3c7', padding: '12px', borderRadius: '6px', border: '1px solid #fde68a', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#92400e' }}>{summary.unique_materials}</div>
            <div style={{ fontSize: '10px', color: '#64748b' }}>Unique Materials</div>
          </div>
        </div>
      )}

      {/* Requirements Table */}
      {requirements.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          No RM requirements found for the selected criteria.
        </div>
      ) : (
        <table className="rm-table">
          <thead>
            <tr>
              <th style={{ width: '5%' }}>#</th>
              <th style={{ width: '25%' }}>RM Grade</th>
              <th style={{ width: '20%' }}>Wire Size</th>
              <th style={{ width: '20%', textAlign: 'right' }}>Total Weight (Kg)</th>
              <th style={{ width: '15%', textAlign: 'right' }}>Order Count</th>
              <th style={{ width: '15%', textAlign: 'right' }}>Total Qty</th>
            </tr>
          </thead>
          <tbody>
            {requirements.map((req, idx) => (
              <tr key={req.rm_grade || `req-${idx}`}>
                <td>{idx + 1}</td>
                <td style={{ fontWeight: 'bold' }}>{req.rm_grade}</td>
                <td>{req.wire_size}</td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold', color: '#1e40af' }}>
                  {req.total_weight_kg?.toLocaleString()}
                </td>
                <td style={{ textAlign: 'right' }}>{req.order_count}</td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{req.total_qty?.toLocaleString()}</td>
              </tr>
            ))}
            {/* Grand Total Row */}
            {summary && (
              <tr className="total-row">
                <td colSpan={3} style={{ textAlign: 'right', fontWeight: 'bold' }}>GRAND TOTAL:</td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold', color: '#1e40af' }}>
                  {summary.total_weight_kg?.toLocaleString()} Kg
                </td>
                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{summary.total_orders}</td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>{summary.total_qty?.toLocaleString()}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Footer */}
      <div style={{ marginTop: '30px', textAlign: 'center', paddingTop: '15px', borderTop: '1px solid #e2e8f0' }}>
        <p style={{ margin: 0, fontSize: '10px', color: '#64748b' }}>
          For queries, please contact: info@singhaniainternational.com | +91 161 4611418
        </p>
        <p style={{ margin: '5px 0 0', fontSize: '9px', color: '#94a3b8' }}>
          www.sturdfix.com | www.singhaniainternational.com
        </p>
      </div>
    </div>
  );
}


